import {
  GamificationLeader,
  GamificationLeaderAchievement,
} from "@axl-dev/school-sdk";
import { ColumnType } from "antd/lib/table";
import { useMemo } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useLoc, useSdk } from "@app/shared/lib/hooks";
import { DateTime, Image, Loc, Table, ValuePlaceholder } from "@app/shared/ui";
import { TruncateItems } from "@app/shared/ui/TruncateItems";

type GamificationAchievementPreviewProps = {
  achievement: GamificationLeaderAchievement;
};
const GamificationAchievementPreview: React.FC<
  GamificationAchievementPreviewProps
> = ({ achievement }) => {
  const loc = useLoc();

  return (
    <div
      className="relative"
      title={
        achievement.title ||
        (achievement.subtype &&
          loc.word(`Gamification.achievement.subtype.${achievement.subtype}`, {
            default: achievement.subtype,
          }))
      }
    >
      <Image
        fit="fill"
        cloudKey={achievement.imageCloudKey}
        width={40}
        height={40}
        resize={{ h: 40, w: 40 }}
        className="br-xxs"
      />
      {achievement.count > 1 && (
        <div
          className="absolute bg-white br-full p-3 border-primary border fs-10 flex flex-center fw-500 color-black z-2"
          style={{ right: -10, top: -10, width: 20, height: 20 }}
        >
          x{achievement.count}
        </div>
      )}
    </div>
  );
};

export const GamificationLeaderTable: React.FC = () => {
  const sdk = useSdk();

  const columns = useMemo(() => {
    return [
      {
        title: <Loc word="GamificationLeader.place" default="Place" />,
        width: "10%",
        align: "right",
        render: (_text, item) => {
          return <span>{item.place}</span>;
        },
      },
      {
        title: <Loc word="GamificationLeader.achievements" />,
        width: "20%",
        render: (_text, item) => {
          return (
            <TruncateItems<GamificationLeaderAchievement>
              items={item.achievements}
              restItemsClassName="flex gap-5 flex-wrap"
            >
              {(x, i) => (
                <GamificationAchievementPreview key={i} achievement={x} />
              )}
            </TruncateItems>
          );
        },
      },
      {
        title: <Loc word="Global.contact" />,
        width: "30%",
        align: "right",
        render: (_text, item) => {
          return <ValuePlaceholder value={item.contact.fullName} />;
        },
      },
      {
        title: <Loc word="GamificationLeader.totalPoints" />,
        align: "right",
        width: "20%",
        render: (_text, item) => {
          return <div className="fw-500">{item.score}</div>;
        },
      },
      {
        title: <Loc word="Global.createdDate" />,
        width: "20%",
        align: "right",
        render: (_text, item) => {
          return <DateTime date={item.createdDate} />;
        },
      },
    ] as ColumnType<GamificationLeader>[];
  }, []);

  return (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={async (filters) => {
        // @ts-expect-error -- strage filter
        const res = await sdk.gamification.leader.fetch({
          ...filters,
          sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
        });

        return {
          items: res.body.items,
          filters: {
            ...res.body.filter,
            sortType:
              res.body.filter.sortType === "Desc"
                ? SortingDirection.desc
                : SortingDirection.asc,
            pageTotal: sdk.gamification.leader.filter.pageTotal,
            page: sdk.gamification.leader.filter.page,
          },
        };
      }}
      rowBordered={false}
      hidePaginationOnePage
      columns={columns}
      scroll={{ x: "max-content" }}
    />
  );
};
